import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useBackendLinksProvider } from "../../../../../Providers/BackendLinksProvider"
import { useEndpointProvider } from "../../../../../Providers/EndpointProvider"
import {
	Deployment,
	useEnvironmentVariablesProvider,
} from "../../../../../Providers/EnvironmentVariablesProvider"
import { useWorkspacesProvider } from "../../../../../Providers/WorkspacesProvider"
import type { PrefetchFileEntry, PrefetchSnapshot, StartPreloadResponse } from "./types"

const POLLING_STATES = ["queued", "running", "cancelled", "unloading"]

const EMPTY_SNAPSHOT: PrefetchSnapshot = {
	storage: {
		current_size: 0,
		max_total_size: 0,
		available_for_new_prefetch: 0,
	},
	files: [],
}

export const prefetchKeys = {
	snapshot: (workspace: string) => ["prefetch-snapshot", workspace] as const,
}

type PrefetchSnapshotOptions = {
	refetchInterval?: number | false
}

export const usePrefetchSnapshot = (options: PrefetchSnapshotOptions = {}) => {
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const { deployment } = useEnvironmentVariablesProvider()
	const { selectedWorkspace } = useWorkspacesProvider()
	const queryKey = prefetchKeys.snapshot(selectedWorkspace)

	const query = useQuery<PrefetchSnapshot>({
		queryKey,
		queryFn: () => endpointProvider.get(
			LINKS.ADMIN.PREFETCH.SNAPSHOT,
			{}
		) as Promise<PrefetchSnapshot>,
		enabled: deployment !== Deployment.CLINICAL && !!selectedWorkspace,
		refetchInterval: options.refetchInterval !== undefined
			? options.refetchInterval
			: (data) => data && data.files.some((file) => POLLING_STATES.includes(file.state))
				? 2000
				: false,
	})

	return { ...query, snapshot: query.data ?? EMPTY_SNAPSHOT }
}

type StartPreloadContext = {
	previous: PrefetchSnapshot | undefined
}

const queuedPlaceholder = (dataObjectId: string): PrefetchFileEntry => ({
	data_object_id: dataObjectId,
	name: "",
	patient_id: "",
	obj_type: null,
	size: 0,
	state: "queued",
	percent_loaded: 0,
	queue_position: null,
	last_used: null,
})

const withQueuedPlaceholders = (
	snapshot: PrefetchSnapshot,
	dataObjectIds: string[],
): PrefetchSnapshot => {
	const missing = dataObjectIds.filter(
		(dataObjectId) => !snapshot.files.some((file) => file.data_object_id === dataObjectId),
	)
	if (missing.length === 0) {
		return snapshot
	}

	return { ...snapshot, files: [...snapshot.files, ...missing.map(queuedPlaceholder)] }
}

export const useStartPreload = () => {
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const { selectedWorkspace } = useWorkspacesProvider()
	const queryClient = useQueryClient()
	const queryKey = prefetchKeys.snapshot(selectedWorkspace)

	return useMutation<StartPreloadResponse, unknown, number | string, StartPreloadContext>({
		mutationFn: (id) => endpointProvider.post(
			LINKS.ADMIN.PREFETCH.SNAPSHOT,
			{ data_object_id: String(id), include_associated: true }
		) as Promise<StartPreloadResponse>,
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData<PrefetchSnapshot>(queryKey)

			queryClient.setQueryData<PrefetchSnapshot>(queryKey, (current) =>
				withQueuedPlaceholders(current ?? EMPTY_SNAPSHOT, [String(id)]),
			)

			return { previous }
		},
		onSuccess: (response) => {
			queryClient.setQueryData<PrefetchSnapshot>(queryKey, (current) =>
				withQueuedPlaceholders(current ?? EMPTY_SNAPSHOT, response.data_object_ids ?? []),
			)
		},
		onError: (_error, _id, context) => {
			if (context?.previous === undefined) {
				queryClient.removeQueries({ queryKey, exact: true })
				return
			}

			queryClient.setQueryData(queryKey, context.previous)
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey }),
	})
}

export const useUnloadFiles = () => {
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const { selectedWorkspace } = useWorkspacesProvider()
	const queryClient = useQueryClient()
	const queryKey = prefetchKeys.snapshot(selectedWorkspace)

	return useMutation<PrefetchSnapshot, unknown, string[]>({
		mutationFn: (dataObjectIds) => endpointProvider.post(
			LINKS.ADMIN.PREFETCH.UNLOAD,
			{ data_object_ids: dataObjectIds }
		) as Promise<PrefetchSnapshot>,
		onSuccess: (response) => {
			queryClient.setQueryData(queryKey, response)
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey }),
	})
}
